import React from 'react';
import { Link } from 'react-router-dom';
import { Container, Row, Col } from 'reactstrap';
import Navbar from './Navbar';
import Footer from './Footer';
import GuestChatWidget from './GuestChatWidget';
import './landing.scss';
import SeoHead from '../../../Components/Common/SeoHead';
import HelpBreadcrumbSchema from '../../../Components/Common/HelpBreadcrumbSchema';
import HelpScreenshot from './HelpScreenshot';
import type { HelpImage } from './HelpScreenshot';

const HelpArticleAmazonFilters: React.FC = () => {
  const BASE = '/images/help/amazon-filters';

  const screenshots: Record<string, HelpImage[]> = {
    step1: [
      { src: `${BASE}/af-step-1.png`, alt: 'Amazon filters overview', caption: 'Step 1 – Why Amazon filters help you source better products' },
    ],
    step2: [
      { src: `${BASE}/af-step-2.png`, alt: 'Seller type filter settings', caption: 'Step 2 – Filter by seller type: FBA, FBM, or Amazon direct' },
    ],
    step3: [
      { src: `${BASE}/af-step-3.png`, alt: 'Condition filter settings', caption: 'Step 3 – Filter by product condition: new, used, or refurbished' },
    ],
    step4: [
      { src: `${BASE}/af-step-4.png`, alt: 'Delivery and Prime filter settings', caption: 'Step 4 – Filter by delivery type and Prime eligibility' },
    ],
    step5: [
      { src: `${BASE}/af-step-5.png`, alt: 'Price filter settings', caption: 'Step 5 – Set price boundaries to protect your profit margin' },
    ],
    step6: [
      { src: `${BASE}/af-step-6.png`, alt: 'Filter priority and behaviour settings', caption: 'Step 6 – How Coderom applies filters when multiple rules conflict' },
    ],
  };

  return (
    <div className="layout-wrapper landing">
      <SeoHead
        title="Amazon Supplier Filters | CodeRom Help"
        description="Filter Amazon products by Prime, FBA, rating & more before listing on eBay."
      />
      <HelpBreadcrumbSchema slug="amazon-filters" name="Amazon supplier filters" />
      <Navbar />

      <div className="help-article-page">
        <Container>
          <Row className="justify-content-center">
            <Col lg={9} xl={8}>
              {/* Breadcrumb */}
              <div className="breadcrumb-section">
                <Link to="/help" className="breadcrumb-link">Help Center</Link>
                <span className="breadcrumb-separator">›</span>
                <span className="breadcrumb-current">Amazon supplier filters</span>
              </div>

              {/* Article Header */}
              <div className="article-header">
                <h1>Amazon supplier filters</h1>
                <p className="article-subtitle">
                  Control which Amazon offers Coderom uses for your eBay listings. Filter by seller type, condition, delivery speed, and more to ensure you always source from reliable suppliers.
                </p>
                <div className="article-meta">
                  <span className="read-time">📖 7 min read</span>
                  <span className="category">Amazon Filters</span>
                </div>
              </div>

              {/* Table of Contents */}
              <div className="table-of-contents">
                <h3>📋 On this page</h3>
                <ul>
                  <li><a href="#step1">1. Why use Amazon filters?</a></li>
                  <li><a href="#step2">2. Seller type filters</a></li>
                  <li><a href="#step3">3. Condition filters</a></li>
                  <li><a href="#step4">4. Delivery and Prime filters</a></li>
                  <li><a href="#step5">5. Price filters</a></li>
                  <li><a href="#step6">6. Filter priority and behaviour</a></li>
                </ul>
              </div>

              {/* Article Content */}
              <div className="article-content">

                {/* Step 1 */}
                <div id="step1" className="content-section">
                  <h2>Step 1: Why use Amazon filters?</h2>
                  <p>
                    Not all Amazon offers are equal. A product might be available from:
                  </p>
                  <ul>
                    <li>Amazon directly (most reliable)</li>
                    <li>FBA sellers (fulfilled by Amazon — fast delivery)</li>
                    <li>Third-party sellers (variable quality and delivery times)</li>
                    <li>Used or refurbished condition</li>
                  </ul>
                  <p>
                    Amazon filters let you control which offers Coderom considers when setting your eBay price and checking stock. This protects you from unreliable suppliers and ensures consistent delivery times for your eBay buyers.
                  </p>

                  <HelpScreenshot images={screenshots.step1} stepLabel="Step 1: Why use Amazon filters?" />
                </div>

                {/* Step 2 */}
                <div id="step2" className="content-section">
                  <h2>Step 2: Seller type filters</h2>
                  <p>
                    Go to <strong>Settings → Amazon Filters</strong> and configure seller preferences:
                  </p>

                  <div className="seller-table">
                    <table>
                      <thead>
                        <tr>
                          <th>Filter</th>
                          <th>What it does</th>
                          <th>Recommended</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr>
                          <td><strong>Amazon only</strong></td>
                          <td>Only use offers sold by Amazon.co.uk</td>
                          <td>Off (too restrictive)</td>
                        </tr>
                        <tr>
                          <td><strong>FBA only</strong></td>
                          <td>Only use offers fulfilled by Amazon (FBA)</td>
                          <td>On</td>
                        </tr>
                        <tr>
                          <td><strong>Require Prime</strong></td>
                          <td>Only use offers eligible for Prime delivery</td>
                          <td>On</td>
                        </tr>
                        <tr>
                          <td><strong>Exclude third-party</strong></td>
                          <td>Skip offers from non-FBA third-party sellers</td>
                          <td>Optional</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>

                  <HelpScreenshot images={screenshots.step2} stepLabel="Step 2: Seller type filters" />

                  <div className="tip-box">
                    <div className="tip-icon">💡</div>
                    <div className="tip-content">
                      <strong>Tip:</strong> We recommend enabling "FBA only" or "Require Prime" at minimum. This ensures fast, reliable delivery from Amazon to your eBay buyers.
                    </div>
                  </div>
                </div>

                {/* Step 3 */}
                <div id="step3" className="content-section">
                  <h2>Step 3: Condition filters</h2>
                  <p>
                    Control which product conditions are acceptable:
                  </p>

                  <div className="condition-table">
                    <table>
                      <thead>
                        <tr>
                          <th>Condition</th>
                          <th>Description</th>
                          <th>Recommended</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr>
                          <td><strong>New</strong></td>
                          <td>Brand new, sealed</td>
                          <td>On (always)</td>
                        </tr>
                        <tr>
                          <td><strong>Renewed/Refurbished</strong></td>
                          <td>Professionally refurbished</td>
                          <td>Off (unless you list as refurbished on eBay)</td>
                        </tr>
                        <tr>
                          <td><strong>Used</strong></td>
                          <td>Previously owned</td>
                          <td>Off</td>
                        </tr>
                        <tr>
                          <td><strong>Open box</strong></td>
                          <td>New but opened packaging</td>
                          <td>Off</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>

                  <HelpScreenshot images={screenshots.step3} stepLabel="Step 3: Condition filters" />

                  <div className="tip-box">
                    <div className="tip-icon">💡</div>
                    <div className="tip-content">
                      <strong>Tip:</strong> If you list products as "New" on eBay, make sure you only accept "New" condition from Amazon. Sending a refurbished item when the buyer expects new will result in returns and negative feedback.
                    </div>
                  </div>
                </div>

                {/* Step 4 */}
                <div id="step4" className="content-section">
                  <h2>Step 4: Delivery and Prime filters</h2>
                  <p>
                    Control delivery requirements:
                  </p>

                  <div className="delivery-table">
                    <table>
                      <thead>
                        <tr>
                          <th>Setting</th>
                          <th>What it does</th>
                          <th>Recommended</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr>
                          <td><strong>Require Prime</strong></td>
                          <td>Only use Prime-eligible offers</td>
                          <td>On</td>
                        </tr>
                        <tr>
                          <td><strong>Maximum delivery days</strong></td>
                          <td>Skip offers with delivery longer than X days</td>
                          <td>3 days</td>
                        </tr>
                        <tr>
                          <td><strong>Free delivery only</strong></td>
                          <td>Only use offers with free delivery</td>
                          <td>Optional</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>

                  <HelpScreenshot images={screenshots.step4} stepLabel="Step 4: Delivery and Prime filters" />
                </div>

                {/* Step 5 */}
                <div id="step5" className="content-section">
                  <h2>Step 5: Price filters</h2>
                  <p>
                    Set price boundaries for Amazon offers:
                  </p>

                  <div className="price-table">
                    <table>
                      <thead>
                        <tr>
                          <th>Setting</th>
                          <th>What it does</th>
                          <th>Recommended</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr>
                          <td><strong>Maximum price</strong></td>
                          <td>Ignore offers above this price</td>
                          <td>Based on your margins</td>
                        </tr>
                        <tr>
                          <td><strong>Minimum price</strong></td>
                          <td>Ignore suspiciously cheap offers</td>
                          <td>Set low (e.g. £1)</td>
                        </tr>
                        <tr>
                          <td><strong>Price change alert</strong></td>
                          <td>Notify if price changes more than X%</td>
                          <td>30%</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>

                  <HelpScreenshot images={screenshots.step5} stepLabel="Step 5: Price filters" />
                </div>

                {/* Step 6 */}
                <div id="step6" className="content-section">
                  <h2>Step 6: Filter priority and behaviour</h2>
                  <p>
                    When multiple Amazon offers match your filters, Coderom selects the best one based on:
                  </p>
                  <ol>
                    <li><strong>Price</strong> — lowest price first</li>
                    <li><strong>Seller type</strong> — Amazon &gt; FBA &gt; Third-party</li>
                    <li><strong>Delivery speed</strong> — fastest delivery first</li>
                    <li><strong>Seller rating</strong> — higher-rated sellers preferred</li>
                  </ol>
                  <p>
                    If no offers match your filters, the product is marked as "out of stock" on eBay — this protects you from sourcing from unacceptable suppliers.
                  </p>

                  <HelpScreenshot images={screenshots.step6} stepLabel="Step 6: Filter priority and behaviour" />
                </div>

                {/* What's Next */}
                <div className="whats-next-section">
                  <h2>What's next?</h2>
                  <p>Now that you've set up quality filters, explore these features:</p>
                  <div className="next-steps">
                    <Link to="/help/orders" className="next-step-card">
                      <div className="next-step-icon">📋</div>
                      <div className="next-step-content">
                        <h4>Order management & fulfilment</h4>
                        <p>Automatically process eBay orders and track shipments</p>
                      </div>
                    </Link>
                    <Link to="/help/safety-protection" className="next-step-card">
                      <div className="next-step-icon">🛡️</div>
                      <div className="next-step-content">
                        <h4>Safety & Protection</h4>
                        <p>Protect your store from VeRO claims with blocked brands and keywords</p>
                      </div>
                    </Link>
                    <Link to="/help/stock" className="next-step-card">
                      <div className="next-step-icon">📦</div>
                      <div className="next-step-content">
                        <h4>Stock synchronisation & inventory</h4>
                        <p>Keep your eBay listings in sync with Amazon stock levels 24/7</p>
                      </div>
                    </Link>
                  </div>
                </div>

                {/* Was This Helpful */}
                <div className="helpful-section">
                  <h3>Was this helpful?</h3>
                  <div className="helpful-buttons">
                    <button className="helpful-btn helpful-yes">👍 Yes</button>
                    <button className="helpful-btn helpful-no">👎 No</button>
                  </div>
                  <p className="helpful-text">
                    Still need help? <Link to="/help">Browse all articles</Link> or{' '}
                    <a href="mailto:support@coderom.com">contact support</a>.
                  </p>
                </div>

              </div>
            </Col>
          </Row>
        </Container>
      </div>

      <style>{`
        .help-article-page {
          padding: 120px 0 80px;
          background: linear-gradient(180deg, #f8fafc 0%, #ffffff 100%);
          min-height: 100vh;
        }

        /* Breadcrumb */
        .breadcrumb-section {
          margin-bottom: 24px;
          font-size: 14px;
        }
        .breadcrumb-link {
          color: #6366f1;
          text-decoration: none;
          transition: color 0.2s;
        }
        .breadcrumb-link:hover {
          color: #4f46e5;
        }
        .breadcrumb-separator {
          margin: 0 8px;
          color: #cbd5e1;
        }
        .breadcrumb-current {
          color: #64748b;
        }

        /* Article Header */
        .article-header {
          margin-bottom: 40px;
        }
        .article-header h1 {
          font-size: 42px;
          font-weight: 700;
          color: #1e293b;
          margin-bottom: 16px;
          line-height: 1.2;
        }
        .article-subtitle {
          font-size: 18px;
          color: #64748b;
          line-height: 1.6;
          margin-bottom: 20px;
        }
        .article-meta {
          display: flex;
          gap: 16px;
          align-items: center;
          font-size: 14px;
        }
        .read-time {
          color: #64748b;
        }
        .category {
          background: #f1f5f9;
          color: #6366f1;
          padding: 4px 12px;
          border-radius: 12px;
          font-weight: 500;
        }

        /* Table of Contents */
        .table-of-contents {
          background: #f8fafc;
          border: 1px solid #e2e8f0;
          border-radius: 12px;
          padding: 24px;
          margin-bottom: 40px;
        }
        .table-of-contents h3 {
          font-size: 16px;
          font-weight: 600;
          color: #1e293b;
          margin-bottom: 16px;
        }
        .table-of-contents ul {
          list-style: none;
          padding: 0;
          margin: 0;
        }
        .table-of-contents li {
          margin-bottom: 8px;
        }
        .table-of-contents a {
          color: #6366f1;
          text-decoration: none;
          font-size: 15px;
          transition: color 0.2s;
        }
        .table-of-contents a:hover {
          color: #4f46e5;
          text-decoration: underline;
        }

        /* Article Content */
        .article-content {
          color: #334155;
          font-size: 16px;
          line-height: 1.8;
        }
        .content-section {
          margin-bottom: 48px;
          scroll-margin-top: 100px;
        }
        .content-section h2 {
          font-size: 28px;
          font-weight: 700;
          color: #1e293b;
          margin-bottom: 16px;
          margin-top: 40px;
        }
        .content-section h3 {
          font-size: 20px;
          font-weight: 600;
          color: #334155;
          margin-top: 24px;
          margin-bottom: 12px;
        }
        .content-section p {
          margin-bottom: 16px;
        }
        .content-section ul,
        .content-section ol {
          margin-bottom: 16px;
          padding-left: 24px;
        }
        .content-section li {
          margin-bottom: 8px;
        }
        .content-section strong {
          color: #1e293b;
          font-weight: 600;
        }

        /* Tip Box */
        .tip-box {
          display: flex;
          gap: 12px;
          background: #eff6ff;
          border-left: 4px solid #3b82f6;
          padding: 16px;
          border-radius: 8px;
          margin: 24px 0;
        }
        .tip-icon {
          font-size: 20px;
          flex-shrink: 0;
        }
        .tip-content {
          font-size: 15px;
          line-height: 1.6;
          color: #1e40af;
        }

        /* Tables */
        .seller-table,
        .condition-table,
        .delivery-table,
        .price-table {
          margin: 24px 0;
          overflow-x: auto;
        }
        .seller-table table,
        .condition-table table,
        .delivery-table table,
        .price-table table {
          width: 100%;
          border-collapse: collapse;
          background: white;
          border-radius: 8px;
          overflow: hidden;
          box-shadow: 0 1px 3px rgba(0,0,0,0.1);
        }
        .seller-table th,
        .condition-table th,
        .delivery-table th,
        .price-table th {
          background: #f8fafc;
          color: #1e293b;
          font-weight: 600;
          text-align: left;
          padding: 12px 16px;
          border-bottom: 2px solid #e2e8f0;
          font-size: 14px;
        }
        .seller-table td,
        .condition-table td,
        .delivery-table td,
        .price-table td {
          padding: 12px 16px;
          border-bottom: 1px solid #f1f5f9;
          font-size: 15px;
        }
        .seller-table tbody tr:last-child td,
        .condition-table tbody tr:last-child td,
        .delivery-table tbody tr:last-child td,
        .price-table tbody tr:last-child td {
          border-bottom: none;
        }
        .seller-table tbody tr:hover,
        .condition-table tbody tr:hover,
        .delivery-table tbody tr:hover,
        .price-table tbody tr:hover {
          background: #f8fafc;
        }

        /* What's Next Section */
        .whats-next-section {
          margin-top: 60px;
          padding-top: 40px;
          border-top: 2px solid #e2e8f0;
        }
        .whats-next-section h2 {
          font-size: 28px;
          font-weight: 700;
          color: #1e293b;
          margin-bottom: 16px;
        }
        .next-steps {
          display: flex;
          flex-direction: column;
          gap: 16px;
          margin-top: 24px;
        }
        .next-step-card {
          display: flex;
          gap: 16px;
          padding: 20px;
          background: white;
          border: 1px solid #e2e8f0;
          border-radius: 12px;
          text-decoration: none;
          transition: all 0.3s;
        }
        .next-step-card:hover {
          border-color: #6366f1;
          box-shadow: 0 4px 12px rgba(99, 102, 241, 0.15);
          transform: translateY(-2px);
        }
        .next-step-icon {
          font-size: 32px;
          flex-shrink: 0;
        }
        .next-step-content h4 {
          font-size: 18px;
          font-weight: 600;
          color: #1e293b;
          margin-bottom: 4px;
        }
        .next-step-content p {
          font-size: 14px;
          color: #64748b;
          margin: 0;
        }

        /* Was This Helpful */
        .helpful-section {
          margin-top: 60px;
          padding: 32px;
          background: #f8fafc;
          border-radius: 12px;
          text-align: center;
        }
        .helpful-section h3 {
          font-size: 20px;
          font-weight: 600;
          color: #1e293b;
          margin-bottom: 20px;
        }
        .helpful-buttons {
          display: flex;
          gap: 12px;
          justify-content: center;
          margin-bottom: 16px;
        }
        .helpful-btn {
          padding: 10px 24px;
          border: 2px solid #e2e8f0;
          background: white;
          border-radius: 8px;
          font-size: 15px;
          font-weight: 500;
          cursor: pointer;
          transition: all 0.2s;
        }
        .helpful-btn:hover {
          border-color: #6366f1;
          background: #eff6ff;
        }
        .helpful-text {
          font-size: 14px;
          color: #64748b;
          margin: 0;
        }
        .helpful-text a {
          color: #6366f1;
          text-decoration: none;
        }
        .helpful-text a:hover {
          text-decoration: underline;
        }

        /* Responsive */
        @media (max-width: 768px) {
          .help-article-page {
            padding: 100px 0 60px;
          }
          .article-header h1 {
            font-size: 32px;
          }
          .article-subtitle {
            font-size: 16px;
          }
          .content-section h2 {
            font-size: 24px;
          }
          .seller-table,
          .condition-table,
          .delivery-table,
          .price-table {
            font-size: 14px;
          }
          .next-step-card {
            flex-direction: column;
            text-align: center;
          }
        }
      `}</style>

      <Footer />
      <GuestChatWidget />
    </div>
  );
};

export default HelpArticleAmazonFilters;
